import type {
  DeepFolderResult,
  DeepResult,
  DeepWorkspaceResult,
  FolderSearchHit,
  FuzzyResult,
  ShortcutSearchHit,
  WorkspaceSearchHit,
} from "./types";
import type { SearchFilesystemJs, SearchFolderRowJs, SearchShortcutJs } from "@/generated/models";
import {
  ProjectHitType,
} from "@/generated/models";

export function deepToShortcutHit(
  hit: DeepResult,
  shortcuts: Map<string, SearchShortcutJs>,
): ShortcutSearchHit | null {
  const shortcut = shortcuts.get(hit.Shortcut.toUpperCase());
  if (shortcut === undefined) return null;
  const metadata = shortcut.Shortcut;
  const path = shortcut.Path;
  switch (hit.kind) {
    case "project":
      return {
        id: hit.Id,
        kind: hit.kind,
        path,
        title: hit.HitType !== ProjectHitType.Name ? metadata.Name : hit.Highlight,
        detail: hit.HitType !== ProjectHitType.Name ? hit.Highlight : undefined,
        metadata,
        score: hit.Score,
      };
    case "page":
      return {
        id: hit.Id,
        kind: hit.kind,
        path,
        title: metadata.Name,
        detail: hit.Highlight,
        metadata,
        deepInfo: { pageId: hit.ShortPageId },
        score: hit.Score,
      };
    case "rp":
      return {
        id: hit.Id,
        kind: hit.kind,
        path,
        title: metadata.Name,
        detail: hit.Highlight,
        metadata,
        deepInfo: { pageName: hit.PageName, pageId: hit.ShortPageId },
        score: hit.Score,
      };
    case "canvas":
      return {
        id: hit.Id,
        kind: hit.kind,
        path,
        title: metadata.Name,
        detail: hit.Highlight,
        metadata,
        deepInfo: { objectId: hit.ObjectId },
        score: hit.Score,
      };
    case "doc":
      return {
        id: hit.Id,
        kind: hit.kind,
        path,
        title: metadata.Name,
        detail: hit.Highlight,
        metadata,
        deepInfo: { nodeId: hit.TextNodeId },
        score: hit.Score,
      };
    case "comment":
      return {
        id: hit.Id,
        kind: hit.kind,
        path,
        title: metadata.Name,
        detail: hit.Highlight,
        metadata,
        deepInfo: { pageName: hit.PageName, pageId: hit.PageId, issueCode: hit.IssueCode },
        score: hit.Score,
      };
    case "note":
      return {
        id: hit.Id,
        kind: hit.kind,
        path,
        title: metadata.Name,
        detail: hit.Highlight,
        metadata,
        score: hit.Score,
      };
    default:
      throw new Error(`Unhandled kind ${hit}`);
  }
}

export function deepToWorkspaceHit(
  hit: DeepWorkspaceResult,
  workspaces: Map<string, SearchFilesystemJs>,
): WorkspaceSearchHit | null {
  const workspace = workspaces.get(hit.FilesystemId);
  if (workspace === undefined) return null;
  const metadata = workspace.Filesystem;
  const path = workspace.Path;
  return {
    id: hit.Id,
    kind: hit.kind,
    path,
    title: hit.Highlight,
    metadata,
    score: hit.Score,
  };
}

export function deepToFolderHit(
  hit: DeepFolderResult,
  folders: Map<string, SearchFolderRowJs>,
): FolderSearchHit | null {
  const metadata = folders.get(hit.NodeId)!;
  if (metadata === undefined) return null;
  return {
    id: hit.Id,
    kind: hit.kind,
    path: metadata.Path,
    title: hit.Highlight,
    metadata,
    score: hit.Score,
  };
}

export function fuzzyShortcutToHit(result: FuzzyResult<SearchShortcutJs>): ShortcutSearchHit {
  return {
    id: result.Shortcut.Shortcut,
    kind: "project",
    path: result.Path,
    title: result.highlight,
    metadata: result.Shortcut,
  };
}

export function fuzzyFolderToHit(result: FuzzyResult<SearchFolderRowJs>): FolderSearchHit {
  return {
    id: result.Folder.FolderId,
    kind: "folder",
    path: result.Path,
    title: result.highlight,
    metadata: result,
  };
}

export function fuzzyWorkspaceToHit(result: FuzzyResult<SearchFilesystemJs>): WorkspaceSearchHit {
  return {
    id: result.Filesystem.FilesystemId,
    kind: "workspace",
    path: result.Path,
    title: result.highlight,
    metadata: result.Filesystem,
  };
}
